import { useState, useEffect, useMemo, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LayoutGrid, Network, TrendingUp, Users, Wallet, Rocket,
  ChevronDown, ChevronRight, UserPlus, UserCheck, Award, Copy, Check,
  Loader2, AlertCircle, Building2, GraduationCap, Image as ImageIcon,
  Gift, Repeat, ArrowRight, Info,
} from 'lucide-react';
import {
  getDashboardData,
  getTeamData,
  getCommissions,
} from '@/services/mlmApi';

/* ───────────────────────── Brand + rank tokens ───────────────────────── */

const LIME = '#BDD630';
const BAOBAB = '#567D38';
const SAND = '#F7F5EE';
const INK = '#1A2E12';
const TERRACOTTA = '#E07A5F';
const CLAY = '#D9B38C';
const MUTED = '#6B7B6E';

/** Rank ladder + colours come from the design system, not the API. The API only
 *  ever tells us which rank the agent is on (`mlm_tier`, lowercase). */
const RANK_LADDER = ['bronze', 'silver', 'gold', 'platinum', 'diamond'] as const;
const RANK_COLORS: Record<string, string> = {
  bronze: '#b08d57',
  silver: '#9aa5a0',
  gold: '#f4c95d',
  platinum: '#cfd9d0',
  diamond: '#8fd6c9',
};

/* ───────────────────────── Backend payload shapes ───────────────────────── */

interface RankRow {
  name?: string;
  min_pv?: number;
  commission_percent?: number;
  discount_percent?: number;
  bonus_eligible?: boolean;
}

interface WalletTxn {
  id?: string;
  type?: string;
  amount?: number;
  balance_after?: number;
  status?: string;
  description?: string;
  reference?: string;
  commission_level?: number | null;
  customer_name?: string | null;
  created_at?: string;
}

interface DashboardPayload {
  agent?: {
    agent_code?: string | null;
    first_name?: string | null;
    approved_at?: string | null;
    mlm_tier?: string | null;
    commission_balance?: number;
    personal_volume?: number;
    group_volume?: number;
  };
  rank?: { current?: RankRow | null; next?: RankRow | null; progress_pct?: number; pv_needed?: number };
  stats?: { commission_pending?: number; team_size?: number; customers_referred?: number };
  recent_transactions?: WalletTxn[];
  referral_url?: string;
}

interface TeamRow {
  id?: string;
  first_name?: string | null;
  surname?: string | null;
  email?: string | null;
  agent_code?: string | null;
  agent_status?: string | null;
  mlm_tier?: string | null;
  personal_volume?: number;
  commission_balance?: number;
  agent_approved_at?: string | null;
  sponsor_id?: string | null;
}

interface TeamPayload {
  level1?: TeamRow[];
  level2?: TeamRow[];
  totals?: { level1_count?: number; level2_count?: number; level1_pv?: number };
}

interface CommissionsPayload {
  balance?: number;
  totals?: { total_earned?: number; total_pending?: number; total_withdrawn?: number };
  transactions?: WalletTxn[];
}

/* ───────────────────────── Formatting helpers ───────────────────────── */

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const arr = <T,>(v: T[] | undefined | null): T[] => (Array.isArray(v) ? v : []);

function money(v: unknown): string {
  return `R${num(v).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
}
function count(v: unknown): string {
  return num(v).toLocaleString('en-ZA');
}
function titleRank(rank?: string | null): string {
  if (!rank) return 'Unranked';
  return rank.charAt(0).toUpperCase() + rank.slice(1).toLowerCase();
}
function fullName(row: TeamRow): string {
  const n = `${row.first_name ?? ''} ${row.surname ?? ''}`.trim();
  return n || 'Team member';
}
function initial(name?: string | null): string {
  const t = (name ?? '').trim();
  return t ? t.charAt(0).toUpperCase() : '?';
}
function shortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
}
function monthKey(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}
/** A member counts as "active" when their agent account is approved. The backend
 *  does not expose a per-cycle activity flag, so this is the closest true signal. */
function isActive(row: TeamRow): boolean {
  return (row.agent_status ?? '') === 'approved';
}

/* ───────────────────────── Small presentational pieces ───────────────────────── */

function RankPill({ rank, size = 'sm' }: { rank?: string | null; size?: 'sm' | 'xs' }) {
  const key = (rank ?? '').toLowerCase();
  const bg = RANK_COLORS[key] ?? CLAY;
  return (
    <span
      className={`inline-flex items-center rounded-full font-display font-bold uppercase tracking-[0.05em] ${
        size === 'xs' ? 'text-[10px] px-2 py-[2px]' : 'text-[11px] px-2.5 py-[3px]'
      }`}
      style={{ background: bg, color: INK }}
    >
      {titleRank(rank)}
    </span>
  );
}

function PortalKpi({
  icon: Icon, label, value, sub,
}: { icon: typeof Wallet; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl bg-white p-5 border" style={{ borderColor: `${CLAY}33` }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${LIME}33` }}>
        <Icon className="w-[18px] h-[18px]" style={{ color: BAOBAB }} />
      </div>
      <div className="font-display font-bold text-2xl" style={{ color: INK }}>{value}</div>
      <div className="text-[13px]" style={{ color: MUTED }}>{label}</div>
      {sub ? <div className="text-xs font-semibold mt-1" style={{ color: BAOBAB }}>{sub}</div> : null}
    </div>
  );
}

function ReqBar({
  label, current, target, prefix = '',
}: { label: string; current: number; target: number; prefix?: string }) {
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1.5 text-[13px]">
        <span style={{ color: MUTED }}>{label}</span>
        <span className="font-semibold" style={{ color: INK }}>
          {prefix}{current.toLocaleString('en-ZA', { maximumFractionDigits: 0 })} / {prefix}{target.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: `${CLAY}55` }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 100 ? LIME : BAOBAB }} />
      </div>
    </div>
  );
}

/** Honest placeholder for anything the backend cannot supply. */
function Unavailable({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-[13px]"
      style={{ background: SAND, color: MUTED, border: `1px dashed ${CLAY}` }}
    >
      <Info className="w-4 h-4 mt-[1px] flex-shrink-0" style={{ color: CLAY }} />
      <span>{children}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-display uppercase tracking-[0.06em] text-xs" style={{ color: BAOBAB }}>
      {children}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-white p-6 border ${className}`} style={{ borderColor: `${CLAY}33` }}>
      {children}
    </div>
  );
}

/* ───────────────────────── Genealogy tree ───────────────────────── */

interface TreeNodeData {
  key: string;
  name: string;
  rank?: string | null;
  pv: number;
  active: boolean;
  children: TreeNodeData[];
}

function TreeRow({ node, depth }: { node: TreeNodeData; depth: number }) {
  const [open, setOpen] = useState(depth < 2);
  const hasKids = node.children.length > 0;
  return (
    <div>
      <div
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 mb-2 border"
        style={{
          marginLeft: depth * 24,
          background: depth === 0 ? `${LIME}26` : '#fff',
          borderColor: `${CLAY}33`,
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          disabled={!hasKids}
          aria-label={hasKids ? (open ? 'Collapse leg' : 'Expand leg') : 'No downline'}
          className="w-6 h-6 flex-shrink-0 rounded-md flex items-center justify-center"
          style={{ background: hasKids ? `${CLAY}44` : 'transparent', cursor: hasKids ? 'pointer' : 'default' }}
        >
          {hasKids ? (
            open
              ? <ChevronDown className="w-[15px] h-[15px]" style={{ color: BAOBAB }} />
              : <ChevronRight className="w-[15px] h-[15px]" style={{ color: BAOBAB }} />
          ) : null}
        </button>
        <span
          className="relative w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center font-display font-bold text-[13px] text-white"
          style={{ background: BAOBAB }}
        >
          {initial(node.name)}
          <span
            className="absolute -bottom-px -right-px w-3 h-3 rounded-full border-2 border-white"
            style={{ background: node.active ? LIME : CLAY }}
          />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" style={{ color: INK }}>{node.name}</div>
          <div className="text-xs" style={{ color: MUTED }}>
            PV {count(node.pv)} · GV not tracked per member
            {hasKids ? ` · ${node.children.length} direct` : ''}
          </div>
        </div>
        <RankPill rank={node.rank} size="xs" />
      </div>
      {open && hasKids ? node.children.map(c => <TreeRow key={c.key} node={c} depth={depth + 1} />) : null}
    </div>
  );
}

/* ───────────────────────── Tabs ───────────────────────── */

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
  { id: 'genealogy', label: 'Genealogy', icon: Network },
  { id: 'commissions', label: 'Commissions', icon: TrendingUp },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'wallet', label: 'Wallet', icon: Wallet },
  { id: 'grow', label: 'Grow', icon: Rocket },
] as const;

type TabId = typeof TABS[number]['id'];

/* ───────────────────────── Page ───────────────────────── */

export function AgentPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: TabId = TABS.some(t => t.id === rawTab) ? (rawTab as TabId) : 'dashboard';
  const setTab = (next: TabId) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'dashboard') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: false });
  };

  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null);
  const [team, setTeam] = useState<TeamPayload | null>(null);
  const [commissions, setCommissions] = useState<CommissionsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // These endpoints return the backend payload directly — there is no
        // `.data` envelope. Team/commissions are allowed to fail independently
        // so that a partial outage still renders the dashboard.
        const d = await getDashboardData();
        if (cancelled) return;
        setDashboard(d ?? {});
        const [t, c] = await Promise.allSettled([getTeamData(), getCommissions()]);
        if (cancelled) return;
        if (t.status === 'fulfilled') setTeam(t.value ?? {});
        if (c.status === 'fulfilled') setCommissions(c.value ?? {});
      } catch (err) {
        if (!cancelled) setError((err as Error)?.message || 'Could not load your agent portal.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── Derived values (all from real payloads) ── */

  const agent = dashboard?.agent;
  const rank = dashboard?.rank;
  const stats = dashboard?.stats;
  const currentRank = (agent?.mlm_tier ?? rank?.current?.name ?? '').toLowerCase();
  const nextRank = rank?.next?.name ?? null;
  const pv = num(agent?.personal_volume);
  const gv = num(agent?.group_volume);
  const balance = num(commissions?.balance ?? agent?.commission_balance);
  const pending = num(commissions?.totals?.total_pending ?? stats?.commission_pending);
  const lifetime = num(commissions?.totals?.total_earned);
  const progressPct = Math.max(0, Math.min(100, num(rank?.progress_pct)));
  const referralUrl = dashboard?.referral_url ?? '';

  const level1 = useMemo(() => arr(team?.level1), [team]);
  const level2 = useMemo(() => arr(team?.level2), [team]);
  const txns = useMemo(() => arr(commissions?.transactions), [commissions]);
  const recentTxns = useMemo(
    () => (txns.length ? txns : arr(dashboard?.recent_transactions)),
    [txns, dashboard],
  );

  const downlineTotal = level1.length + level2.length || num(stats?.team_size);
  const activeLegs = useMemo(() => level1.filter(isActive).length, [level1]);

  const newThisMonth = useMemo(() => {
    const nowKey = monthKey(new Date().toISOString());
    return level1.filter(m => monthKey(m.agent_approved_at) === nowKey).length;
  }, [level1]);

  const topPerformer = useMemo(() => {
    if (!level1.length) return null;
    return level1.reduce((best, m) => (num(m.personal_volume) > num(best.personal_volume) ? m : best), level1[0]);
  }, [level1]);

  const tree = useMemo<TreeNodeData | null>(() => {
    if (!agent) return null;
    const kids: TreeNodeData[] = level1.map(m => ({
      key: m.id ?? fullName(m),
      name: fullName(m),
      rank: m.mlm_tier,
      pv: num(m.personal_volume),
      active: isActive(m),
      children: level2
        .filter(g => g.sponsor_id && m.id && g.sponsor_id === m.id)
        .map(g => ({
          key: g.id ?? fullName(g),
          name: fullName(g),
          rank: g.mlm_tier,
          pv: num(g.personal_volume),
          active: isActive(g),
          children: [],
        })),
    }));
    return {
      key: 'root',
      name: `${agent.first_name ?? 'You'} (You)`,
      rank: currentRank,
      pv,
      active: true,
      children: kids,
    };
  }, [agent, level1, level2, currentRank, pv]);

  /** Itemise the current month's real wallet transactions by source. */
  const commissionBreakdown = useMemo(() => {
    const nowKey = monthKey(new Date().toISOString());
    const thisMonth = txns.filter(t => monthKey(t.created_at) === nowKey && num(t.amount) > 0);
    const buckets = new Map<string, { label: string; note: string; amount: number; n: number }>();
    for (const t of thisMonth) {
      const lvl = t.commission_level;
      const key = lvl ? `level-${lvl}` : (t.type ?? 'other');
      const label = lvl
        ? `Level ${lvl} unilevel`
        : t.type === 'commission'
          ? 'Personal sales commission'
          : titleRank(t.type ?? 'other');
      const prev = buckets.get(key);
      buckets.set(key, {
        label,
        note: lvl ? `Generation ${lvl} downline volume` : 'Credited to your wallet',
        amount: num(prev?.amount) + num(t.amount),
        n: num(prev?.n) + 1,
      });
    }
    return [...buckets.values()].sort((a, b) => b.amount - a.amount);
  }, [txns]);

  const commissionTotal = useMemo(
    () => commissionBreakdown.reduce((s, c) => s + c.amount, 0),
    [commissionBreakdown],
  );

  /** Last 6 months of real earned commission, from wallet transactions. */
  const monthlySeries = useMemo(() => {
    const totals = new Map<string, number>();
    const now = new Date();
    const keys: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      keys.push(k);
      totals.set(k, 0);
    }
    for (const t of txns) {
      const k = monthKey(t.created_at);
      if (k && totals.has(k) && num(t.amount) > 0) totals.set(k, num(totals.get(k)) + num(t.amount));
    }
    return keys.map(k => ({ key: k, label: monthLabel(k).split(' ')[0], value: num(totals.get(k)) }));
  }, [txns]);

  const seriesMax = useMemo(
    () => monthlySeries.reduce((m, p) => Math.max(m, p.value), 0),
    [monthlySeries],
  );

  /** Group all commission transactions into per-month cycles. */
  const cycleHistory = useMemo(() => {
    const byMonth = new Map<string, { earned: number; n: number }>();
    for (const t of txns) {
      const k = monthKey(t.created_at);
      if (!k || num(t.amount) <= 0) continue;
      const prev = byMonth.get(k);
      byMonth.set(k, { earned: num(prev?.earned) + num(t.amount), n: num(prev?.n) + 1 });
    }
    return [...byMonth.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .slice(0, 6)
      .map(([k, v]) => ({ key: k, label: monthLabel(k), earned: v.earned, n: v.n }));
  }, [txns]);

  function copyReferral() {
    if (!referralUrl) return;
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => { /* clipboard denied — leave button unchanged */ });
  }

  /* ── Loading / error ── */

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" style={{ background: SAND }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: BAOBAB }} />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" style={{ background: SAND }}>
        <div className="max-w-md w-full rounded-2xl bg-white p-8 text-center border" style={{ borderColor: `${CLAY}44` }}>
          <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: TERRACOTTA }} />
          <h1 className="font-serif text-2xl mb-2" style={{ color: INK }}>Agent portal unavailable</h1>
          <p className="text-sm" style={{ color: MUTED }}>
            {error || 'We could not load your agent record. Approved agent access is required.'}
          </p>
        </div>
      </div>
    );
  }

  /* ── Render ── */

  return (
    <div className="min-h-[70vh]" style={{ background: SAND }}>
      {/* Portal header */}
      <div style={{ background: `linear-gradient(135deg, ${INK}, ${BAOBAB})` }}>
        <div className="max-w-7xl mx-auto px-6 pt-9">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center font-display font-bold text-2xl"
                style={{ background: LIME, color: INK }}
              >
                {initial(agent.first_name)}
              </div>
              <div>
                <div className="font-display uppercase tracking-[0.1em] text-[11px]" style={{ color: LIME }}>
                  Agent Portal{agent.agent_code ? ` · ID ${agent.agent_code}` : ''}
                </div>
                <h1 className="font-serif text-3xl text-white mt-0.5 mb-1.5">
                  Welcome back{agent.first_name ? `, ${agent.first_name}` : ''}
                </h1>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <RankPill rank={currentRank} />
                  <span className="text-[13px] text-white/70">
                    {nextRank
                      ? `${progressPct}% to ${titleRank(nextRank)}`
                      : 'Top rank reached'}
                  </span>
                </div>
              </div>
            </div>
            <div className="rounded-2xl px-5 py-3.5 text-right border border-white/15 bg-white/10">
              <div className="text-xs text-white/65">Available to withdraw</div>
              <div className="font-serif text-3xl font-bold text-white">{money(balance)}</div>
            </div>
          </div>

          <div className="flex gap-6 mt-6 overflow-x-auto border-b-2 border-white/10">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  aria-current={active ? 'page' : undefined}
                  className="inline-flex items-center gap-2 py-3.5 -mb-0.5 whitespace-nowrap font-display font-semibold text-[13px] uppercase tracking-[0.05em] border-b-2"
                  style={{
                    color: active ? LIME : 'rgba(255,255,255,0.7)',
                    borderColor: active ? LIME : 'transparent',
                  }}
                >
                  <Icon className="w-[15px] h-[15px]" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-7">

        {/* ── DASHBOARD ── */}
        {tab === 'dashboard' && (
          <div className="flex flex-col gap-5">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
              <PortalKpi icon={Wallet} label="Personal volume (PV)" value={count(pv)} />
              <PortalKpi icon={Users} label="Group volume (GV)" value={money(gv)} />
              <PortalKpi icon={TrendingUp} label="Cycle commission" value={money(commissionTotal)} sub="This month" />
              <PortalKpi icon={Network} label="Total downline" value={count(downlineTotal)} sub={`${activeLegs} active legs`} />
              <PortalKpi icon={UserPlus} label="New this month" value={count(newThisMonth)} sub="recruits" />
            </div>

            <div className="grid gap-4 grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
              <Card>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <SectionTitle>Rank progress</SectionTitle>
                  <span className="font-serif text-lg" style={{ color: BAOBAB }}>
                    {titleRank(currentRank)}{nextRank ? ` → ${titleRank(nextRank)}` : ''}
                  </span>
                </div>

                <div className="flex flex-col gap-4 mb-5">
                  {num(rank?.next?.min_pv) > 0 ? (
                    <ReqBar label="Personal volume" current={pv} target={num(rank?.next?.min_pv)} />
                  ) : (
                    <Unavailable>
                      No higher rank threshold is configured — you are on the top rank.
                    </Unavailable>
                  )}
                  <Unavailable>
                    Group-volume and active-leg qualification targets are not defined in the rank
                    plan yet, so no target bars can be shown. Your current GV is {money(gv)} across{' '}
                    {count(activeLegs)} active {activeLegs === 1 ? 'leg' : 'legs'}.
                  </Unavailable>
                </div>

                <div className="flex items-center gap-1.5 rounded-xl px-3.5 py-2.5" style={{ background: SAND }}>
                  {RANK_LADDER.map(r => {
                    const idx = RANK_LADDER.indexOf(r);
                    const curIdx = RANK_LADDER.indexOf(currentRank as typeof RANK_LADDER[number]);
                    const reached = curIdx >= 0 && idx <= curIdx;
                    const isCur = idx === curIdx;
                    return (
                      <Fragment key={r}>
                        <div className="flex-1 text-center">
                          <div
                            className="w-[22px] h-[22px] rounded-full mx-auto mb-1"
                            style={{
                              background: reached ? RANK_COLORS[r] : `${CLAY}55`,
                              border: isCur ? `2px solid ${BAOBAB}` : 'none',
                            }}
                          />
                          <span
                            className="text-[10px]"
                            style={{ color: reached ? INK : MUTED, fontWeight: isCur ? 700 : 400 }}
                          >
                            {titleRank(r)}
                          </span>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </Card>

              <Card>
                <SectionTitle>Commission earned · last 6 months</SectionTitle>
                <p className="text-xs mt-1 mb-4" style={{ color: MUTED }}>
                  Historic group volume is not stored per cycle, so this charts your real wallet credits.
                </p>
                {seriesMax > 0 ? (
                  <div className="flex items-end gap-2.5 h-[150px]">
                    {monthlySeries.map((p, i) => (
                      <div key={p.key} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                        <span className="text-[10px]" style={{ color: MUTED }}>{p.value ? money(p.value) : ''}</span>
                        <div
                          className="w-full rounded-t-md"
                          style={{
                            height: `${Math.max(2, (p.value / seriesMax) * 100)}%`,
                            background: i === monthlySeries.length - 1 ? LIME : BAOBAB,
                            minHeight: 6,
                          }}
                        />
                        <span className="text-[10px]" style={{ color: MUTED }}>{p.label}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Unavailable>No commission has been credited in the last six months yet.</Unavailable>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* ── GENEALOGY ── */}
        {tab === 'genealogy' && (
          <Card>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <SectionTitle>Your network</SectionTitle>
                <p className="text-[13px] mt-1" style={{ color: MUTED }}>
                  Expand each leg to see its downline. The API exposes two generations
                  (direct sponsors and their recruits).
                </p>
              </div>
              <div className="flex gap-4 text-xs" style={{ color: MUTED }}>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: LIME }} /> Active agent
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: CLAY }} /> Not yet approved
                </span>
              </div>
            </div>
            {tree && tree.children.length > 0 ? (
              <TreeRow node={tree} depth={0} />
            ) : (
              <Unavailable>
                You have no downline yet. Share your referral link from the Grow tab to start building your team.
              </Unavailable>
            )}
          </Card>
        )}

        {/* ── COMMISSIONS ── */}
        {tab === 'commissions' && (
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-[1.4fr_1fr]">
            <Card>
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
                <SectionTitle>Current cycle · {monthLabel(monthKey(new Date().toISOString()) ?? '')}</SectionTitle>
                <span className="font-serif text-2xl font-bold" style={{ color: BAOBAB }}>{money(commissionTotal)}</span>
              </div>
              {commissionBreakdown.length > 0 ? (
                commissionBreakdown.map(c => (
                  <div
                    key={c.label}
                    className="flex items-center justify-between gap-4 py-3.5 border-b last:border-b-0"
                    style={{ borderColor: `${CLAY}33` }}
                  >
                    <div>
                      <div className="font-semibold text-sm" style={{ color: INK }}>{c.label}</div>
                      <div className="text-xs" style={{ color: MUTED }}>
                        {c.note} · {c.n} {c.n === 1 ? 'entry' : 'entries'}
                      </div>
                    </div>
                    <span className="font-bold whitespace-nowrap" style={{ color: BAOBAB }}>{money(c.amount)}</span>
                  </div>
                ))
              ) : (
                <Unavailable>
                  No commission has been credited this cycle yet. Rank and leadership-pool bonuses are
                  not itemised by the backend — only wallet credits are shown.
                </Unavailable>
              )}
            </Card>

            <Card className="h-fit">
              <SectionTitle>Cycle history</SectionTitle>
              <div className="mt-3">
                {cycleHistory.length > 0 ? (
                  cycleHistory.map(h => (
                    <div
                      key={h.key}
                      className="flex items-center justify-between gap-4 py-2.5 border-b last:border-b-0"
                      style={{ borderColor: `${CLAY}33` }}
                    >
                      <div>
                        <div className="font-semibold text-sm" style={{ color: INK }}>{h.label}</div>
                        <div className="text-xs" style={{ color: MUTED }}>
                          {h.n} {h.n === 1 ? 'credit' : 'credits'} · per-cycle GV not tracked
                        </div>
                      </div>
                      <div className="font-bold text-sm" style={{ color: BAOBAB }}>{money(h.earned)}</div>
                    </div>
                  ))
                ) : (
                  <Unavailable>No commission cycles recorded yet.</Unavailable>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* ── TEAM ── */}
        {tab === 'team' && (
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
              <PortalKpi icon={UserCheck} label="Active legs" value={count(activeLegs)} sub="No leg target configured" />
              <PortalKpi icon={UserPlus} label="Personally sponsored" value={count(level1.length)} />
              <PortalKpi
                icon={Award}
                label="Top performer"
                value={topPerformer ? fullName(topPerformer) : '—'}
                sub={topPerformer ? `${count(topPerformer.personal_volume)} PV` : 'No team members yet'}
              />
            </div>

            <div className="rounded-2xl bg-white overflow-hidden border" style={{ borderColor: `${CLAY}33` }}>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      {['Member', 'Rank', 'PV', 'Commission balance', 'Status'].map(h => (
                        <th
                          key={h}
                          className="text-left px-4 py-3 font-display uppercase tracking-[0.05em] text-[11px] border-b whitespace-nowrap"
                          style={{ color: MUTED, borderColor: `${CLAY}33` }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {level1.length > 0 ? level1.map(m => (
                      <tr key={m.id ?? fullName(m)}>
                        <td className="px-4 py-3 border-b" style={{ borderColor: `${CLAY}33` }}>
                          <div className="flex items-center gap-2.5">
                            <span
                              className="w-8 h-8 rounded-full flex items-center justify-center font-display font-bold text-xs text-white flex-shrink-0"
                              style={{ background: BAOBAB }}
                            >
                              {initial(m.first_name)}
                            </span>
                            <span className="font-semibold text-sm" style={{ color: INK }}>{fullName(m)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b" style={{ borderColor: `${CLAY}33` }}>
                          <RankPill rank={m.mlm_tier} size="xs" />
                        </td>
                        <td className="px-4 py-3 border-b text-sm" style={{ borderColor: `${CLAY}33`, color: INK }}>
                          {count(m.personal_volume)}
                        </td>
                        <td className="px-4 py-3 border-b text-sm font-bold" style={{ borderColor: `${CLAY}33`, color: BAOBAB }}>
                          {money(m.commission_balance)}
                        </td>
                        <td className="px-4 py-3 border-b" style={{ borderColor: `${CLAY}33` }}>
                          <span
                            className="text-xs font-semibold rounded-full px-2.5 py-1"
                            style={{
                              background: isActive(m) ? `${LIME}40` : `${TERRACOTTA}22`,
                              color: isActive(m) ? BAOBAB : TERRACOTTA,
                            }}
                          >
                            {isActive(m) ? 'Active' : titleRank(m.agent_status ?? 'pending')}
                          </span>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: MUTED }}>
                          You have not sponsored anyone yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── WALLET ── */}
        {tab === 'wallet' && (
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-[1fr_1.4fr]">
            <div className="flex flex-col gap-4">
              <div
                className="rounded-3xl p-6 text-white"
                style={{ background: `linear-gradient(135deg, ${BAOBAB}, ${INK})` }}
              >
                <div className="text-[13px] text-white/70">Available balance</div>
                <div className="font-serif text-4xl font-bold mt-1 mb-4">{money(balance)}</div>
                <a
                  href="/member/agent/payouts"
                  className="block w-full text-center rounded-xl px-4 py-2.5 font-semibold text-sm"
                  style={{ background: LIME, color: INK }}
                >
                  Request a withdrawal
                </a>
                <div className="flex justify-between mt-4 text-[13px]">
                  <span className="text-white/70">Pending</span>
                  <span className="font-bold">{money(pending)}</span>
                </div>
                <div className="flex justify-between mt-2 text-[13px]">
                  <span className="text-white/70">Lifetime earned</span>
                  <span className="font-bold">{money(lifetime)}</span>
                </div>
              </div>

              <Card className="!p-5">
                <SectionTitle>Payout method</SectionTitle>
                <div className="mt-3 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${LIME}33` }}>
                    <Building2 className="w-[18px] h-[18px]" style={{ color: BAOBAB }} />
                  </div>
                  <Unavailable>
                    Banking details are not returned by the agent API. Payout destinations are captured
                    when you submit a withdrawal request.
                  </Unavailable>
                </div>
              </Card>
            </div>

            <Card>
              <SectionTitle>Transaction history</SectionTitle>
              <div className="mt-3">
                {recentTxns.length > 0 ? recentTxns.map((t, i) => {
                  const amt = num(t.amount);
                  const positive = amt >= 0;
                  return (
                    <div
                      key={t.id ?? `${t.reference ?? 'txn'}-${i}`}
                      className="flex items-center justify-between gap-4 py-3 border-b last:border-b-0"
                      style={{ borderColor: `${CLAY}33` }}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate" style={{ color: INK }}>
                          {t.description || titleRank(t.type ?? 'Transaction')}
                        </div>
                        <div className="text-xs" style={{ color: MUTED }}>
                          {shortDate(t.created_at)}
                          {t.status ? ` · ${titleRank(t.status)}` : ''}
                          {t.customer_name ? ` · ${t.customer_name}` : ''}
                        </div>
                      </div>
                      <span className="font-bold whitespace-nowrap" style={{ color: positive ? BAOBAB : TERRACOTTA }}>
                        {positive ? '+' : '−'}{money(Math.abs(amt))}
                      </span>
                    </div>
                  );
                }) : (
                  <Unavailable>No wallet transactions yet.</Unavailable>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* ── GROW ── */}
        {tab === 'grow' && (
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            <Card>
              <SectionTitle>Your referral link</SectionTitle>
              <p className="text-[13px] mt-1.5 mb-3.5" style={{ color: MUTED }}>
                Share to earn commission on every new customer or recruit you bring in.
              </p>
              {referralUrl ? (
                <div className="flex gap-2.5">
                  <input
                    readOnly
                    value={referralUrl}
                    aria-label="Your referral link"
                    className="flex-1 h-11 px-3.5 rounded-xl text-sm border"
                    style={{ borderColor: `${CLAY}55`, background: SAND, color: INK }}
                  />
                  <button
                    type="button"
                    onClick={copyReferral}
                    className="inline-flex items-center gap-2 px-4 rounded-xl font-semibold text-sm whitespace-nowrap"
                    style={{ background: LIME, color: INK }}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              ) : (
                <Unavailable>Your referral link is not available yet.</Unavailable>
              )}
              <div className="mt-5">
                <Unavailable>
                  A scannable QR code for in-person sign-ups is not generated by the backend yet.
                </Unavailable>
              </div>
            </Card>

            <Card>
              <SectionTitle>Growth tools</SectionTitle>
              <div className="flex flex-col gap-2.5 mt-3.5">
                {([
                  [ImageIcon, 'Marketing asset library', 'Shareable posts and stories'],
                  [GraduationCap, 'Agent Academy', 'Training lessons and certification'],
                  [Gift, 'Fast-start incentive', 'Bonus for early active recruits'],
                  [Repeat, 'Autoship builder', 'Lock in recurring team volume'],
                ] as const).map(([Icon, title, sub]) => (
                  <div
                    key={title}
                    className="flex items-center gap-3.5 rounded-xl p-3.5 border"
                    style={{ background: SAND, borderColor: `${CLAY}44` }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${LIME}33` }}>
                      <Icon className="w-[19px] h-[19px]" style={{ color: BAOBAB }} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm" style={{ color: INK }}>{title}</div>
                      <div className="text-xs" style={{ color: MUTED }}>{sub} · coming soon</div>
                    </div>
                    <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: CLAY }} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentPortal;
